import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LassieContext } from "../../../context/LassieProvider";
import { lassieDashboardAPI, lightningAPI } from "../../../api";
import WplInput from "../../../components/wpl-input/WplInput";
import OlMap from "../../../components/ol-map/OlMap";
import TurbineLightningRuler from "../../../components/turbine-lightning-ruler/TurbineLightningRuler";
import AlertPopup from "../../../components/alert-popup/AlertPopup";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import MultiSelectDropdown from "../../../components/multiselection-dropdown/MultiSelectDropdown";
import Card from "../../../components/card/Card";
import RiskTimelineGraph from "../../lightning/lightning-windfarm/RiskTimelineGraph";
import ArrowAnimation from "../../../components/arrow-animation/ArrowAnimation";
import LightningTable from "../lightning-table/LightningTable";
import DataAvailableSince from "../../../components/data-available-since/DataAvailableSince";
import WindfarmTimeNote from "../../../components/windfarm-time-note/WindfarmTimeNote";
import { useSyncSelectedWindfarm } from "../../../hooks/useSyncSelectedWindfarm";
import {
  toZonedDateStr,
  toZonedIso,
  windfarmTimeLabel,
  windfarmTimeZone,
  zonedMidnightIso,
} from "../../../prettyDate";
import "./historic-lightning-analysis.css";

// Where the distance filter starts. It sits above the search button with the other three, so
// the query is only ever run with values the user has seen.
const DEFAULT_MAX_DISTANCE_M = 2500;

const CERTIFICATION_PARAM = {
  "Inside certification": "inside",
  "Outside certification": "outside",
};

const LIGHTNING_TYPE_PARAM = {
  "cloud-to-ground": "cloud-to-ground",
  "intra-cloud": "intra-cloud",
};

// The dashboard endpoint hands the collection area over already parsed; the lighter turbine
// endpoint sends the raw "POLYGON((lat lon,lat lon,...))" that MySQL produced. OlMap expects
// the parsed shape, so do here what the backend's parse_turbines does there.
const parseCollectionArea = (wkt) => {
  if (!wkt || typeof wkt !== "string") return [];

  const inside = wkt.slice(wkt.indexOf("((") + 2, wkt.lastIndexOf("))"));
  if (!inside) return [];

  return inside
    .split(",")
    .map((pair) => {
      const [lat, lon] = pair.trim().split(/\s+/).map(Number);
      return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
    })
    .filter(Boolean);
};

// The filters as the backend wants them. null means "no restriction", which axios leaves out
// of the query string. Anything the backend would not recognise becomes null rather than
// being sent as-is - WplInput reports an empty value while you are typing in a dropdown.
const toServerFilters = (filters) => ({
  max_distance_m: Number(filters.distance.value) || null,
  min_probability_of_hit: Number(filters.probability_of_hit.value) || null,
  lightning_type: LIGHTNING_TYPE_PARAM[filters.cloud_to_ground.value] || null,
  certification: CERTIFICATION_PARAM[filters.outside_certification.value] || null,
});

export default function HistoricLightningAnalysis() {
  const navigate = useNavigate();
  const { windfarms } = useContext(LassieContext);
  const [windfarmTurbines, setWindfarmTurbines] = useState([]);
  const [showAllTurbines, setShowAllTurbines] = useState(false);
  const [selectedWindfarm, setSelectedWindfarm] = useState(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState({
    from: "",
    to: "",
  });
  // One row per turbine/strike pair: the same strike appears once per turbine in range, each
  // with its own distance and probability, which is what the table lists. The map draws the
  // strikes themselves, and OlMap already collapses the pairs by lightning_id to do it.
  const [strikes, setStrikes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedTurbines, setSelectedTurbines] = useState([]);
  const [riskTimeline, setRiskTimeline] = useState(null);
  const [showRiskGraph, setShowRiskGraph] = useState(false);
  const [filters, setFilters] = useState({
    outside_certification: {
      filter: (tl) => true,
      value: "All",
    },
    distance: {
      filter: (tl) => tl.distance_km * 1000 <= DEFAULT_MAX_DISTANCE_M,
      value: DEFAULT_MAX_DISTANCE_M,
    },
    probability_of_hit: {
      filter: (tl) => tl.probability_of_hit * 100 >= 0,
      value: 0,
    },
    cloud_to_ground: {
      filter: (tl) => tl.cloud_to_ground,
      value: "cloud-to-ground",
    },
  });
  let lastGroupId = null;
  let evenGroup = true;
  const hasData = strikes !== null

  let { windfarm_id, turbine_id } = useParams();
  if (windfarm_id) {
    windfarm_id = parseInt(windfarm_id);
  }
  if (turbine_id) {
    turbine_id = parseInt(turbine_id);
  }

  useEffect(() => {
    resetHistoricData();
  }, [selectedTurbines, selectedTimePeriod]);


  useEffect(() => {
    if (!windfarm_id || !windfarms) return;
    if (selectedWindfarm && selectedWindfarm.id === windfarm_id) return;
    setSelectedWindfarm(windfarms.find((wf) => wf.id === windfarm_id));
  }, [selectedWindfarm, windfarm_id, windfarms]);

  // The turbine list this page needs is just positions and names, for the dropdown and for
  // the icons on the map. The context's fetchTurbines uses the dashboard endpoint, which
  // also aggregates every strike of every turbine - 25MB and 14s of SQL on a large windfarm,
  // enough to time out and leave the map with no turbines to draw. This one is ~0.8s.
  useEffect(() => {
    if (!windfarm_id) return;

    let cancelled = false;
    lightningAPI
      .listTurbinesByWindfarmId(windfarm_id)
      .then((rows) => {
        if (cancelled) return;
        setWindfarmTurbines(
          (rows || []).map((t) => ({
            ...t,
            collection_area: parseCollectionArea(t.collection_area),
          }))
        );
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching turbines for the historic analysis", error);
        setWindfarmTurbines([]);
      });

    return () => { cancelled = true; };
  }, [windfarm_id]);

  // Every time on this page - the table, the CSV and the days picked for the period - is on
  // the wind farm's clock, not the viewer's: a user in New York looking at a Danish wind farm
  // sees the time it was in Denmark. Undefined until the turbines are in; null if none has a
  // position, and then times are in UTC and say so.
  const timeZone = useMemo(
    () => (windfarmTurbines.length ? windfarmTimeZone(windfarmTurbines) : undefined),
    [windfarmTurbines]
  );

  const applyFilters = useCallback((strikes) => {
    if (!strikes) return [];
    return strikes.filter((tl) => {
      for (const [key, filter] of Object.entries(filters)) {
        if (!filter.filter(tl)) return false;
      }
      return true;
    });
  }, [filters]);

  const filteredStrikes = useMemo(
    () => applyFilters(strikes),
    [strikes, applyFilters]
  );


  const selectedTurbinesWithLightning = useMemo(() => {
    if (!selectedTurbines || selectedTurbines.length === 0) return [];

    return selectedTurbines.map((turbine) => ({
      ...turbine,
      turbine_lightning: filteredStrikes.filter(
        (strike) => strike.turbine_id === turbine.id
      ),
    }));
  }, [selectedTurbines, filteredStrikes]);


  const groupedLightningIds = useMemo(() => {
    const idSet = new Set();
    const lightningMap = new Map();

    filteredStrikes.forEach((l) => {
      lightningMap.set(l.lightning_id, l);
    });

    filteredStrikes.forEach((l) => {
      if (l.parent_lightning_id && lightningMap.has(l.parent_lightning_id)) {
        idSet.add(l.lightning_id);
        idSet.add(l.parent_lightning_id);
      }
    });

    return idSet;
  }, [filteredStrikes]);

  const isGetButtonDisabled = !(
    (selectedTimePeriod.from) &&
    (selectedTimePeriod.to) &&
    selectedWindfarm &&
    (showAllTurbines || selectedTurbines.length > 0)
  );


  const isExportButtonDisabled = !(
    selectedWindfarm && filteredStrikes.length
  );

  const getData = async () => {
    if (!selectedWindfarm) return;

    setError(false);
    setLoading(true);

    const payload = {
      from_timestamp: zonedMidnightIso(selectedTimePeriod.from, timeZone),
      to_timestamp: zonedMidnightIso(selectedTimePeriod.to, timeZone),
      windfarm_id: selectedWindfarm.id,
      ...toServerFilters(filters),
    };

    if (!showAllTurbines && selectedTurbines.length > 0) {
      payload.turbine_ids = selectedTurbines.map((t) => t.id).join(",");
    }

    setShowAllTurbines(selectedTurbines.length === 0);

    // One request, narrowed by what was set above the button, feeding both the table and the
    // map - so the two are always showing the same selection.
    try {
      setStrikes(await lassieDashboardAPI.listHistoricLightningActivity(payload));
    } catch (error) {
      console.error("Error fetching historic lightning activity", error);
      setError(true);
    } finally {
    setLoading(false);
    }
  };

  const handleWindfarmChange = (name) => {
    resetHistoricData();
    const wf = windfarms.find((w) => w.name === name);
    if (wf) {
      navigate(`/lassie/historic-lightning/windfarm/${wf.id}`);
    }
  };

  const downloadLightningCSV = useCallback(() => {
    if (!selectedWindfarm) return;

    // Both carry their offset, so a file opened in another country cannot be misread.
    const lightningAnalysis = filteredStrikes.map((s) => ({
      windfarm: selectedWindfarm.name,
      turbine_name: s.turbine_name,
      timestamp_windfarm: toZonedIso(new Date(s.timestamp), timeZone),
      timestamp_utc: toZonedIso(new Date(s.timestamp), null),
      peak_current: Math.round(s.peak_current * 10) / 10,
      distance_meters: Math.round(s.distance_km * 100) * 10,
      latitude: s.lat,
      longitude: s.lon,
      semi_major_meters: Math.round(s.semi_major_axis * 100) * 10,
      semi_minor_meters: Math.round(s.semi_minor_axis * 100) * 10,
      probability_within_collection_area: Math.round(s.probability_of_hit * 100),
      outside_iec_certification: Boolean(s.outside_certification),
      is_subsequent: Boolean(s.parent_lightning_id !== null),
      cloud_to_ground: Boolean(s.cloud_to_ground),
      lightning_id: s.lightning_id,
      parent_lightning_id: s.parent_lightning_id,
    }));

    const csvData =
      "data:text/csv;charset=utf-8," +
      Object.keys(lightningAnalysis[0]).join(",") +
      "\n" +
      lightningAnalysis.map((row) => Object.values(row).join(",")).join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvData));

    let filename = `lassie-${selectedWindfarm.name}`;
    if (!showAllTurbines && selectedTurbines.length > 0) {
      filename += "-" + selectedTurbines.map((t) => t.name).join("-");
    }
    filename += ".csv";

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  }, [filteredStrikes, selectedWindfarm, showAllTurbines, selectedTurbines, timeZone]);

  const updateRiskData = async () => {
    setShowRiskGraph(!showRiskGraph);
    setLoading(true);

    if (!riskTimeline || riskTimeline.length === 0) {
      try {
        const data = await lightningAPI.listWindfarmRisktimeline(windfarm_id);
        setRiskTimeline(data);
      } catch (error) {
        console.log(`There was an error fetching risk timeline: ${error}`);
      }
      finally {
        setLoading(false);
      }
    }
  };

  const resetHistoricData = () => {
    setStrikes(null);
    setRiskTimeline(null);
    setShowRiskGraph(false);
  };


  useSyncSelectedWindfarm(windfarm_id);

  return (
    <div className="historic-lightning-analysis">
      <AlertPopup
        showPopup={error}
        closePopup={setError}
        status="wrong"
        title={"Something went wrong..."}
        buttonText={"Close"}
      >
        <p>
          The requested data is too large to process. Please try again with a
          shorter date range.
        </p>
      </AlertPopup>

      <h1>Historic Lightning Analysis</h1>
      <div className="form-container">
        <div className="select-wrapper">
          <h5 className="label">Select a time period</h5>
          <div className="period-selector">
            <input
              type="date"
              value={selectedTimePeriod.from}
              onChange={(e) =>
                setSelectedTimePeriod({
                  ...selectedTimePeriod,
                  from: e.target.value,
                })
              }
            />
            <input
              type="date"
              value={selectedTimePeriod.to}
              onChange={(e) =>
                setSelectedTimePeriod({
                  ...selectedTimePeriod,
                  to: e.target.value,
                })
              }
            />
          </div>

          <h5 className="label">Select a windfarm</h5>
          <WplInput
            className="no-risk-windfarms"
            options={windfarms.map((wf) => wf.name)}
            onlySelectableOptions
            value={selectedWindfarm && selectedWindfarm.name}
            onChanged={(name) => handleWindfarmChange(name)}
          />

          {/* Next to the period pickers on purpose: it says which range is worth asking for. */}
          {selectedWindfarm && (
            <DataAvailableSince earliestData={selectedWindfarm.earliest_data} />
          )}

          <h5 className="label">Select a turbine</h5>

          <MultiSelectDropdown
            options={["All turbines", ...windfarmTurbines.map((t) => t.name).sort((a, b) => {
              const numA = parseInt(a, 10);
              const numB = parseInt(b, 10);
              if (!isNaN(numA) && !isNaN(numB)) return numA - numB;

              return a.localeCompare(b);
            })]}
            value={
              showAllTurbines
                ? ["All turbines"]
                : selectedTurbines
                  .map((t) => t.name)
                  .sort((a, b) => {
                    const numA = parseInt(a, 10);
                    const numB = parseInt(b, 10);
                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                    return a.localeCompare(b);
                  })
            }
            onChange={(names) => {
              const includesAll = names.includes("All turbines");

              if (includesAll && !showAllTurbines) {
                setSelectedTurbines([]);
                setShowAllTurbines(true);
                return;
              }
              if (showAllTurbines && !includesAll) {
                const selected = windfarmTurbines.filter((t) => names.includes(t.name));
                setSelectedTurbines(selected);
                setShowAllTurbines(false);
                return;
              }

              const cleanedNames = names.filter((n) => n !== "All turbines");
              const selected = windfarmTurbines.filter((t) => cleanedNames.includes(t.name));

              setSelectedTurbines(selected);
              setShowAllTurbines(false);
            }}
          />


        </div>
        <div className="query-filters">
              <WplInput
                title="In-/outside certification"
                options={[
                  "All",
                  "Inside certification",
                  "Outside certification",
                ]}
                onlySelectableOptions
                value={
                  (filters.outside_certification &&
                    filters.outside_certification.value) ||
                  "All"
                }
                onChanged={(value) => {
                  let newFilter;
                  if (value === "All") newFilter = () => true;
                  if (value === "Inside certification")
                    newFilter = (tl) => !tl.outside_certification;
                  if (value === "Outside certification")
                    newFilter = (tl) => tl.outside_certification;
                  setFilters((currentFilterValues) => ({
                    ...currentFilterValues,
                    outside_certification: {
                      filter: newFilter,
                      value,
                    },
                  }));
                }}
              />
              <WplInput
                title="Distance (m)"
                value={filters.distance ? filters.distance.value : DEFAULT_MAX_DISTANCE_M}
                onChanged={(value) => {
                  setFilters((p) => ({
                    ...p,
                    distance: {
                      filter: (tl) =>
                        Math.round(tl.distance_km * 100) * 10 <= value,
                      value,
                    },
                  }));
                }}
              />
              <WplInput
                title="Min probability of hit (%)"
                value={
                  filters.probability_of_hit
                    ? filters.probability_of_hit.value
                    : 0
                }
                onChanged={(value) => {
                  setFilters((p) => ({
                    ...p,
                    probability_of_hit: {
                      filter: (tl) => tl.probability_of_hit * 100 >= value,
                      value,
                    },
                  }));
                }}
              />
              <WplInput
                title="Lightning type"
                options={["All", "cloud-to-ground", "intra-cloud"]}
                onlySelectableOptions
                value={
                  filters.cloud_to_ground
                    ? filters.cloud_to_ground.value
                    : "cloud-to-ground"
                }
                onChanged={(value) => {
                  let newFilter;
                  if (value === "All") newFilter = () => true;
                  if (value === "cloud-to-ground")
                    newFilter = (tl) => tl.cloud_to_ground;
                  if (value === "intra-cloud")
                    newFilter = (tl) => !tl.cloud_to_ground;
                  setFilters((currentFilterValues) => ({
                    ...currentFilterValues,
                    cloud_to_ground: {
                      filter: newFilter,
                      value,
                    },
                  }));
                }}
              />
        </div>

        <div className="button-area">
          <button
            className={`get-btn ${isGetButtonDisabled ? "disabled" : ""}`}
            onClick={() => getData()}
          >
            Get lightning data
          </button>

          <button
            value="Export lightning to .csv"
            className={`export-btn ${isExportButtonDisabled ? "disabled" : ""}`}
            onClick={downloadLightningCSV}
          >
            Download as .csv
          </button>
        </div>
      </div>

      {loading && (
        <div className="historic-loading">
          <TurbineAnimation />
        </div>
      )}

      <div className="windfarm-report">
        {/* The filters drive the map as well as the table, so this block stays up whenever
            either of them has data. */}
        {selectedWindfarm && (hasData || hasData) && (
          <div className="notification-turbine">
            {selectedWindfarm && (
              <h2>
                {selectedWindfarm.name}
              </h2>
            )}
            <WindfarmTimeNote timeZone={timeZone} />

            {hasData &&
              selectedTurbines &&
              filteredStrikes && (
                <TurbineLightningRuler strikes={filteredStrikes} />
              )}

            {hasData && (
              <div className="table-wrapper">
                <div className="historic-table-scroll">
                  <LightningTable
                    loading={loading}
                    fields={[
                      {
                        key: "turbine_name",
                        title: "Turbine",
                      },
                      {
                        key: "distance_km",
                        title: "Distance",
                      },
                      {
                        key: "peak_current",
                        title: "Peak current",
                      },
                      {
                        key: "probability_of_hit",
                        title: "Probability",
                      },
                      {
                        key: "outside_certification",
                        title: "Outside certification",
                      },
                      {
                        key: "parent_lightning_id",
                        title: "Subsequent stroke",
                      },
                      {
                        key: "semi_major_axis",
                        title: "Major axis",
                      },
                      {
                        key: "semi_minor_axis",
                        title: "Minor axis",
                      },
                      {
                        key: "lightning_id",
                        title: "id",
                      },
                      {
                        key: "parent_id_column",
                        title: "parent id",
                      },
                      {
                        key: "timestamp",
                        title: `Timestamp (${windfarmTimeLabel(timeZone).name})`,
                      },
                    ]}
                    items={filteredStrikes}
                    className="lightning-table"
                    virtualize
                    customRenderFunction={(tl, i, sortByColumn) => {
                      let highlightClass = "";

                      const isTurbineLevel =
                        hasData &&
                        !showAllTurbines &&
                        selectedTurbinesWithLightning.collection_area;

                      if (
                        isTurbineLevel &&
                        sortByColumn === "timestamp" &&
                        groupedLightningIds.has(tl.lightning_id)
                      ) {
                        const groupId = tl.parent_lightning_id || tl.lightning_id;

                        if (groupId !== lastGroupId) {
                          evenGroup = !evenGroup;
                          lastGroupId = groupId;
                        }

                        highlightClass = evenGroup ? "flash-highlight-a" : "flash-highlight-b";
                      }

                      return (
                        <tr
                          className={`lightning-row 
                            ${tl.outside_certification ? "outside-cert" : ""} 
                            ${highlightClass}
                          `}
                          key={i}
                        >
                          <td>{tl.turbine_name}</td>
                          <td>{Math.round(tl.distance_km * 100) * 10} m</td>
                          <td>{Math.round(tl.peak_current * 10) / 10} kA</td>
                          <td>{Math.round(tl.probability_of_hit * 100)}%</td>
                          <td>{tl.outside_certification ? "✔" : ""}</td>
                          <td>{tl.parent_lightning_id !== null ? "✔" : " "}</td>
                          <td>{tl.semi_major_axis * 1000} m</td>
                          <td>{tl.semi_minor_axis * 1000} m</td>
                          <td>{tl.lightning_id}</td>
                          <td>{tl.parent_lightning_id}</td>
                          <td>{toZonedDateStr(new Date(tl.timestamp), timeZone, true, true)}</td>
                        </tr>
                      );
                    }}

                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Outside the table's block on purpose: the map has its own data and must not wait
            for the table, nor disappear when the table has nothing to show. */}
        {hasData && !showAllTurbines && (
          <OlMap
            lightning_strikes={filteredStrikes}
            turbines={selectedTurbinesWithLightning}
            isHistoricLightningAnalysis={true}
          />
        )}

        {selectedWindfarm && showAllTurbines && filteredStrikes.length > 0 && (
          <OlMap
            key="main-map"
            lightning_strikes={filteredStrikes}
            turbines={windfarmTurbines}
            isHistoricLightningAnalysis={true}
          />
        )}



      </div>

      {filteredStrikes.length > 0 && <Card
        className={`risk-graph-area ${showRiskGraph ? "open" : "closed"}`}
      >
        <div className="risk-graph-header">
          <h2>Your risk timeline</h2>
          <ArrowAnimation
            showMore={showRiskGraph}
            setShowMore={() => updateRiskData()}
            size={2}
          />
        </div>
        {showRiskGraph && riskTimeline && (
          <RiskTimelineGraph
            riskTimeline={riskTimeline}
            turbine={selectedTurbines}
          />
        )}
      </Card>}
    </div>
  );
}